import { AppLink } from '@/shared/components/AppLink';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { useFullyVisible } from '@/core/hooks/useIntersectionObserver';

function isExternalLink(url: string) {
  return /^https?:\/\//i.test(url);
}

interface AnimatedBoxSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    titleImageUrl?: string;
    boxImageTop?: string;
    boxImageMiddle?: string;
    boxImageBottom?: string;
    link?: string;
    lazyload?: string;
    blankImageUrl?: string;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function AnimatedBoxSection({
  data,
  sectionKey,
  eventLabel,
  priority,
}: AnimatedBoxSectionProps) {
  const title = (data.title || []) as string[];
  const titleImageUrl = data.titleImageUrl as string | undefined;
  const boxImageTop = data.boxImageTop as string | undefined;
  const boxImageMiddle = data.boxImageMiddle as string | undefined;
  const boxImageBottom = data.boxImageBottom as string | undefined;
  const link = data.link || '#';
  const lazyload = data.lazyload;
  const shouldRefresh = data.shouldRefresh as boolean | undefined;
  // Use the generic IntersectionObserver hook to detect when section is visible in viewport
  // Defaults to 100% visible, but can be configured: useFullyVisible({ threshold: 0.8 }) for 80%
  const { ref: boxRef, meetsThreshold: isInView } = useFullyVisible<HTMLDivElement>();

  const eventName = `${sectionKey}_${(title?.[0] || 'AnimatedBox').replace(/\s/g, '')}`;
  const loadingMode = lazyload ? 'lazy' : 'eager';

  const SectionLink = isExternalLink(link) ? (
    <a
      href={link}
      className="absolute inset-0 z-10 ga-event-trigger"
      data-eventname={eventName}
      data-pos={priority}
      data-sectionname={eventLabel}
      aria-label={title.length ? title.join(' ') : 'Animated Box Section'}
      rel="noopener noreferrer"
    >
      &nbsp;
    </a>
  ) : (
    <AppLink
      to={link}
      className="absolute inset-0 z-10 ga-event-trigger"
      data-eventname={eventName}
      data-pos={priority}
      data-sectionname={eventLabel}
      aria-label={title.length ? title.join(' ') : 'Animated Box Section'}
      shouldRefresh={shouldRefresh}
    >
      &nbsp;
    </AppLink>
  );

  return (
    <section
      className="md:hidden relative"
      style={{
        background:
          'linear-gradient(90deg, rgba(254, 238, 241, 1) 0%, rgba(254, 238, 241, 0) 25%, rgba(254, 238, 241, 0) 50%, rgba(254, 238, 241, 0) 75%, rgba(254, 238, 241, 1) 100%)',
      }}
    >
      <div className="container-fluid py-4">
        {/* Title Image */}
        {titleImageUrl && (
          <h2 className="mb-4 font-primary text-base font-normal text-center leading-normal text-[#9D7792]">
            <img
              className="inline-block w-[40vw] h-auto"
              src={resolveImageUrl(titleImageUrl)}
              alt={title.length ? title.join(' ') : ''}
              loading={loadingMode}
              decoding="async"
            />
          </h2>
        )}

        {/* Animated Box Container - Full bleed on mobile */}
        <div
          ref={boxRef}
          className="relative !mt-4 !mb-4 pt-[10px] md:mx-0 w-[160px] h-[210px] mx-auto"
        >
          {/* Middle Image (Ring) */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-1/2 z-0 transition-[bottom] duration-1000 ease-in-out ${
              isInView ? 'bottom-[30px]' : 'bottom-[10px]'
            }`}
          >
            {boxImageMiddle ? (
              <img
                className="w-full h-auto block"
                src={resolveImageUrl(boxImageMiddle)}
                alt=""
                loading={loadingMode}
                decoding="async"
              />
            ) : null}
          </div>

          {/* Top Image (Lid) */}
          <div
            className={`absolute left-1/2 bottom-1/4 -translate-x-1/2 w-full z-10 origin-bottom-right transition-transform duration-1000 ease-in-out ${
              isInView ? 'rotate-[50deg]' : 'rotate-[0deg]'
            }`}
          >
            {boxImageTop ? (
              <img
                className="w-full h-auto block"
                src={resolveImageUrl(boxImageTop)}
                alt=""
                loading={loadingMode}
                decoding="async"
              />
            ) : null}
          </div>

          {/* Bottom Image (Container) */}
          <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-full z-0">
            {boxImageBottom ? (
              <img
                className="w-full h-auto block"
                src={resolveImageUrl(boxImageBottom)}
                alt=""
                loading={loadingMode}
                decoding="async"
              />
            ) : null}
          </div>

          {/* Section Link Overlay */}
          {SectionLink}
        </div>
      </div>
    </section>
  );
}

